import { useEffect, useMemo, useState } from "react";
import { useQuery } from "convex/react";
import { Check, Search, X } from "lucide-react";

import { AppIcon } from "../../components/apps/AppIcon";
import { appCategoryDisplayLabel } from "../../lib/apps/categoryIcons";
import { assignBrandAppColors } from "../../lib/apps/appUsageColors";
import { AppFriendlyName } from "../../lib/apps/AppFriendlyName";
import { insightRawAppKey, toNamespacedAppKey } from "../../lib/apps/appFilterKeys";
import { matchesAppSearchQuery } from "../../lib/apps/matchesAppSearchQuery";
import { isUnattributedApp } from "../../lib/native/mergeSameDaySnapshot";
import { queryRef } from "../../lib/convex/refs";
import type { InsightWindowResult } from "../../lib/insights/insightScoring";
import { BottomSheet } from "../habits/BottomSheet";
import type { FilterEntity } from "../habits/AppFilterSheet";
import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";

type SearchApp = {
  key: string;
  displayName: string;
  appIdentifier?: string;
  category?: string;
};

type InsightExcludeSheetProps = {
  open: boolean;
  onClose: () => void;
  anonymousDeviceId: string;
  windowStartDate: string;
  windowEndDate: string;
  windowData: InsightWindowResult | undefined;
  excludedAppKeys: string[];
  onChange: (next: string[]) => void;
};

const searchUsedAppsRef = queryRef<
  { anonymousDeviceId: string; startDate: string; endDate: string; query?: string },
  SearchApp[]
>("habitAppSearch:searchUsedApps");

const INSIGHT_EXCLUDE_APP_ICON_SIZE = 18;
const INSIGHT_EXCLUDE_APP_ICON_RENDER_SIZE = 27;

function collectWindowEntities(windowData: InsightWindowResult | undefined): FilterEntity[] {
  if (!windowData) return [];
  const byKey = new Map<string, FilterEntity>();

  for (const day of windowData.days) {
    for (const app of day.topApps) {
      if (isUnattributedApp(app)) continue;
      const raw = insightRawAppKey(app);
      if (!raw || app.durationSeconds <= 0) continue;
      const key = toNamespacedAppKey(app);
      const existing = byKey.get(key);
      if (existing) continue;
      byKey.set(key, {
        key,
        name: app.displayName,
        displayName: app.displayName,
        appIdentifier: app.appIdentifier,
        category: app.category,
        isCategory: false,
        color: app.color ?? "#7ab89a",
      });
    }
  }

  const entities = [...byKey.values()];
  const colored = assignBrandAppColors(
    entities.map((entity) => ({
      appIdentifier: entity.appIdentifier,
      displayName: entity.displayName,
      category: entity.category,
    })),
    undefined,
    entities.length,
  );
  return entities.map((entity, index) => {
    const withColor = colored[index] as { color?: string } | undefined;
    return {
      ...entity,
      color: withColor?.color ?? entity.color,
    };
  });
}

export function InsightExcludeSheet({
  open,
  onClose,
  anonymousDeviceId,
  windowStartDate,
  windowEndDate,
  windowData,
  excludedAppKeys,
  onChange,
}: InsightExcludeSheetProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!open) setSearchQuery("");
  }, [open]);

  const excluded = useMemo(() => new Set(excludedAppKeys), [excludedAppKeys]);
  const trimmedSearch = searchQuery.trim();
  const noneExcluded = excluded.size === 0;

  const entities = useMemo(() => collectWindowEntities(windowData), [windowData]);
  const entityKeys = useMemo(() => new Set(entities.map((entity) => entity.key)), [entities]);

  const searchRaw = useQuery(
    searchUsedAppsRef,
    open && trimmedSearch.length > 0
      ? {
          anonymousDeviceId,
          startDate: windowStartDate,
          endDate: windowEndDate,
          query: trimmedSearch,
        }
      : "skip",
  );

  const localMatches = useMemo(
    () =>
      trimmedSearch.length > 0
        ? entities.filter((entity) => matchesAppSearchQuery(entity, trimmedSearch))
        : [],
    [entities, trimmedSearch],
  );

  const historyMatches = useMemo<FilterEntity[]>(() => {
    if (!searchRaw || searchRaw.length === 0) return [];
    const filtered = searchRaw.filter((app) => !entityKeys.has(app.key));
    const colored = assignBrandAppColors(
      filtered.map((app) => ({
        appIdentifier: app.appIdentifier,
        displayName: app.displayName,
        category: app.category,
      })),
      undefined,
      filtered.length,
    );
    return filtered.map((app, index) => {
      const withColor = colored[index] as { color?: string } | undefined;
      return {
        key: app.key,
        name: app.displayName,
        displayName: app.displayName,
        appIdentifier: app.appIdentifier,
        category: app.category,
        isCategory: false,
        color: withColor?.color ?? "#7ab89a",
      };
    });
  }, [entityKeys, searchRaw]);

  const toggle = (key: string) => {
    if (excluded.has(key)) {
      onChange(excludedAppKeys.filter((item) => item !== key));
      return;
    }
    onChange([...excludedAppKeys, key]);
  };

  const includeAll = () => onChange([]);

  return (
    <BottomSheet open={open} onClose={onClose} labelledBy="insight-exclude-title">
      <div className="flex items-center justify-between px-4 pt-4 pb-1">
        <h2 id="insight-exclude-title" className="text-lg font-semibold text-[oklch(92%_0.02_150)]">
          {t.insight.excludeSheetTitle}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="icon-button"
          style={{ minWidth: 36, minHeight: 36, border: "none" }}
          aria-label={t.insight.closeExclude}
        >
          <X size={18} />
        </button>
      </div>

      <div className="max-h-[62svh] overflow-y-auto px-2 pb-2">
        <section className="mt-1">
          <h3 className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-[oklch(52%_0.03_155)]">
            {t.insight.excludeSectionLabel}
          </h3>
          <div className="liquid-glass-inset p-1">
            <p className="px-2 pb-2 text-xs text-[oklch(58%_0.025_155)]">
              {noneExcluded
                ? t.insight.excludeHelperAllIncluded
                : formatTemplate(t.insight.excludeHelperCount, { count: excluded.size })}
            </p>

            <div className="px-2 pb-2">
              <label className="liquid-glass-input-shell relative flex items-center gap-2 px-3 py-2.5">
                <Search size={15} className="shrink-0 text-[oklch(58%_0.025_155)]" aria-hidden />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={t.insight.excludeSearchPlaceholder}
                  className="min-h-11 min-w-0 flex-1 bg-transparent text-base text-[oklch(88%_0.02_150)] outline-none placeholder:text-[oklch(52%_0.025_155)]"
                  aria-label={t.insight.excludeSearchAria}
                />
              </label>
            </div>

            {trimmedSearch.length > 0 ? (
              <div className="pb-1">
                <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[oklch(52%_0.03_155)]">
                  {t.insight.excludeSearchResults}
                </p>
                {localMatches.map((entity) => (
                  <ExcludeRow
                    key={entity.key}
                    entity={entity}
                    checked={excluded.has(entity.key)}
                    onToggle={() => toggle(entity.key)}
                  />
                ))}

                {searchRaw === undefined ? (
                  <p className="px-2 py-2 text-sm text-[oklch(58%_0.025_155)]">{t.insight.excludeSearching}</p>
                ) : historyMatches.length > 0 ? (
                  <>
                    <p className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-[oklch(52%_0.03_155)]">
                      {t.insight.excludeSearchHistoryApps}
                    </p>
                    {historyMatches.map((entity) => (
                      <ExcludeRow
                        key={entity.key}
                        entity={entity}
                        checked={excluded.has(entity.key)}
                        onToggle={() => toggle(entity.key)}
                      />
                    ))}
                  </>
                ) : null}

                {searchRaw !== undefined &&
                localMatches.length === 0 &&
                historyMatches.length === 0 ? (
                  <p className="px-2 py-2 text-sm text-[oklch(58%_0.025_155)]">{t.insight.excludeNoMatchingApps}</p>
                ) : null}
              </div>
            ) : entities.length === 0 ? (
              <p className="px-2 py-3 text-sm text-[oklch(58%_0.025_155)]">{t.insight.excludeNoApps}</p>
            ) : (
              entities.map((entity) => (
                <ExcludeRow
                  key={entity.key}
                  entity={entity}
                  checked={excluded.has(entity.key)}
                  onToggle={() => toggle(entity.key)}
                />
              ))
            )}
          </div>
        </section>
      </div>

      <div className="liquid-glass-sheet-footer flex gap-2 px-4 pt-3">
        <button type="button" onClick={includeAll} className="secondary-action flex-1" disabled={noneExcluded}>
          {t.insight.excludeIncludeAll}
        </button>
        <button type="button" onClick={onClose} className="primary-action flex-1">
          {t.common.done}
        </button>
      </div>
    </BottomSheet>
  );
}

function ExcludeRow({
  entity,
  checked,
  onToggle,
}: {
  entity: FilterEntity;
  checked: boolean;
  onToggle: () => void;
}) {
  const t = getTranslations(useScreen0Language().languageCode);
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-3 rounded-xl px-2 py-2.5 text-left transition-colors active:bg-[oklch(20%_0.02_165)]"
      aria-pressed={checked}
    >
      <AppIcon
        displayName={entity.displayName}
        appIdentifier={entity.appIdentifier}
        category={entity.category}
        color={entity.color}
        size={INSIGHT_EXCLUDE_APP_ICON_SIZE}
        iconSize={INSIGHT_EXCLUDE_APP_ICON_RENDER_SIZE}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-[oklch(88%_0.02_150)]">
          <AppFriendlyName
            displayName={entity.displayName}
            appIdentifier={entity.appIdentifier}
            category={entity.category}
          />
        </div>
        <div className="truncate text-xs text-[oklch(72%_0.025_155)]">
          {appCategoryDisplayLabel(entity.category, t, entity.appIdentifier)}
        </div>
      </div>
      <span
        className={`grid h-5 w-5 shrink-0 place-items-center rounded-md ${
          checked
            ? "bg-[oklch(72%_0.09_155)] text-[oklch(12%_0.015_160)]"
            : "liquid-glass-day-inactive text-transparent"
        }`}
      >
        <Check size={13} />
      </span>
    </button>
  );
}
